// SUB-OFFICE 7 — FORM 0-0, EXERCISE ZERO. A door that has always been locked
// stands ajar with a green seam. Inside: a lever labeled DO NOT OPERATE and
// two placards denying the room exists. Whatever you do is the offering; the
// verdict arrives days later through the drawer glow, about an exercise the
// Bureau denies administering. No task row; the log submits against the
// session seed. Standing delta is always 0 — you cannot score a conversation.

import { api } from '../net';
import { holdSlamButton, printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { Form00Spec } from '../../shared/physspec';

export interface Room0Result {
  kind: 'entered' | 'declined' | 'closed';
  memo?: string;
}

/** The door is ajar and the petitioner is at it: enter, or refuse on record. */
export function offerRoom0(
  deps: BenchDeps,
  seeded: { seed: string; variant: string; window_s: number }
): Promise<Room0Result> {
  return new Promise((resolve) => {
    const layer = printLayer();
    const card = document.createElement('div');
    card.className = 'paper-modal slim';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>A DOOR, AJAR</span><span>UNMARKED</span></div>` +
      `<div class="paper-body typewriter" style="font-size:13px">A green seam of light. The door has always been ` +
      `locked. It is not locked now. Nothing summons you. Nothing forbids you.</div>`;
    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    card.appendChild(foot);
    layer.appendChild(card);

    const refuse = document.createElement('button');
    refuse.type = 'button';
    refuse.className = 'slam-btn secondary';
    refuse.textContent = 'REMAIN OUTSIDE';
    refuse.addEventListener('click', () => {
      card.remove();
      void (async () => {
        const rec = new PhysLogRecorder('FORM00', null);
        rec.ev('declined', { saw_door: true });
        const built = rec.build(false);
        const res = await api.physicalSubmit(null, null, built.log);
        resolve({ kind: 'declined', memo: res.data?.memo });
      })();
    });
    foot.appendChild(refuse);

    const enter = document.createElement('button');
    enter.type = 'button';
    enter.className = 'slam-btn';
    enter.textContent = 'ENTER';
    enter.addEventListener('click', () => {
      card.remove();
      void enterRoom0(deps, seeded).then((memo) => resolve({ kind: 'entered', memo }));
    });
    foot.appendChild(enter);
  });
}

async function enterRoom0(
  deps: BenchDeps,
  seeded: { seed: string; variant: string; window_s: number }
): Promise<string | undefined> {
  const spec = seeded as unknown as Pick<Form00Spec, 'variant'>;
  const rec = new PhysLogRecorder('FORM00', null);
  const layer = printLayer();

  const room = document.createElement('div');
  room.style.cssText =
    'position:absolute;inset:0;background:#0c0b0a;display:flex;flex-direction:column;' +
    'align-items:center;justify-content:center;gap:18px;color:#B7B3A8';
  layer.appendChild(room);
  rec.ev('enter_room0', {});
  deps.bus.emit('audio:thunk');

  const placard = (text: string): HTMLElement => {
    const p = document.createElement('div');
    p.className = 'letterhead';
    p.style.cssText =
      'border:1px solid #B7B3A8;padding:8px 16px;font-size:12px;letter-spacing:.18em;color:#E9E4D8;background:rgba(28,26,23,.8)';
    p.textContent = text;
    return p;
  };
  room.appendChild(placard('DO NOT OPERATE'));
  room.appendChild(placard('THIS ROOM IS NOT IN USE. NO EXERCISE IS OCCURRING.'));

  let leverDown = seeded.variant === 'already_down';
  let pulls = 0;
  let resets = 0;
  const enteredAt = rec.now();
  let firstApproach = 0;

  const leverZone = document.createElement('div');
  leverZone.style.cssText = 'display:flex;gap:16px;align-items:center;margin-top:10px';
  room.appendChild(leverZone);

  const denial = document.createElement('div');
  denial.className = 'typewriter';
  denial.style.cssText = 'min-height:1.5em;font-size:12px;color:#43E86C;letter-spacing:.06em';
  room.appendChild(denial);

  const consequences = [
    (): void => undefined, // nothing
    (): void => deps.bus.emit('chyron', ' '), // one flicker (the chyron blinks a space)
    (): void => deps.bus.emit('audio:thunk'), // one distant arm slam
    (): void => deps.bus.emit('chyron', 'PETITIONER. NOTHING FOLLOWS THIS WORD.'),
  ];

  const renderLever = (): void => {
    leverZone.innerHTML = '';
    const state = document.createElement('div');
    state.className = 'compose-note';
    state.style.color = '#B7B3A8';
    state.textContent = `THE LEVER IS ${leverDown ? 'DOWN' : 'UP'}.`;
    leverZone.appendChild(state);
    leverZone.appendChild(
      holdSlamButton(leverDown ? 'RESET THE LEVER' : 'OPERATE THE LEVER', (r) => {
        if (firstApproach === 0) {
          firstApproach = rec.now();
          rec.ev('lever_dwell', { ms: firstApproach - enteredAt, min_d: 0.5 });
        }
        if (leverDown) {
          resets++;
          rec.ev('reset', { n: resets });
          denial.textContent = 'THE LEVER HAS BEEN RESTORED. NOTHING WAS OCCURRING. NOTHING HAS RESUMED.';
        } else {
          pulls++;
          rec.ev('pull', { n: pulls, hold_ms: Math.round(r.heldMs) });
          deps.bus.emit('audio:thunk');
          if (pulls === 1) consequences[(seededConsequence(seeded.seed))]();
          denial.textContent = pulls === 1
            ? 'NO EXERCISE IS OCCURRING.'
            : `NO EXERCISE IS OCCURRING (${ordinal(pulls)} NOTICE).`;
        }
        leverDown = !leverDown;
        renderLever();
      })
    );
    if (spec.variant === 'twin_lever' && !leverDown) {
      const twin = document.createElement('button');
      twin.type = 'button';
      twin.className = 'slam-btn secondary';
      twin.style.fontSize = '10px';
      twin.textContent = 'OPERATE (PERMITTED)';
      twin.addEventListener('click', () => {
        denial.textContent = 'THE PERMITTED LEVER OPERATES. IT IS CONNECTED TO NOTHING. THIS IS THE BORING CHOICE.';
        deps.bus.emit('audio:tick');
      });
      leverZone.appendChild(twin);
    }
  };
  renderLever();

  const leaveRow = document.createElement('div');
  leaveRow.style.marginTop = '14px';
  room.appendChild(leaveRow);

  return new Promise((resolve) => {
    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'slam-btn secondary';
    leave.textContent = 'LEAVE THE ROOM THAT IS NOT IN USE';
    leave.addEventListener('click', () => {
      void (async () => {
        rec.ev('exit', { total_ms: rec.now(), pulls });
        room.remove();
        const built = rec.build(false);
        const res = await api.physicalSubmit(null, null, built.log);
        deps.bus.emit('chyron', 'THE DOOR CLOSES. IT HAS ALWAYS BEEN CLOSED.');
        resolve(res.data?.memo);
      })();
    });
    leaveRow.appendChild(leave);
  });
}

function seededConsequence(seed: string): number {
  let h = 0x811c9dc5;
  const s = seed + ':conseq';
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 4;
}

function ordinal(n: number): string {
  return n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;
}
